import { create } from 'zustand';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

type ToastType = 'success' | 'error' | 'info';
interface ToastMsg {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastStore {
  toasts: ToastMsg[];
  push: (type: ToastType, message: string) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;
export const useToast = create<ToastStore>((set) => ({
  toasts: [],
  push: (type, message) => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { id, type, message }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 5000);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function ToastContainer() {
  const { toasts, dismiss } = useToast();
  if (!toasts.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cn(
            'flex w-80 items-start gap-3 rounded-lg border bg-white p-3 shadow-lg',
            t.type === 'success' && 'border-green-200',
            t.type === 'error' && 'border-red-200',
            t.type === 'info' && 'border-slate-200',
          )}
        >
          {t.type === 'success' && <CheckCircle2 className="h-5 w-5 flex-none text-green-600" />}
          {t.type === 'error' && <AlertCircle className="h-5 w-5 flex-none text-red-600" />}
          {t.type === 'info' && <AlertCircle className="h-5 w-5 flex-none text-slate-500" />}
          <div className="flex-1 text-sm text-slate-700">{t.message}</div>
          <button onClick={() => dismiss(t.id)} className="text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
